import { gql } from "@apollo/client";
import { client } from "../../../../graphql/client";

// 获取门禁码功能的灰度配置值
export const GET_ACCESS_CODE_FEATURE_CONFIG = gql`
    query GetAccessCodeFeatureConfig {
        getAccessCodeFeatureConfig
    }
`;

export interface GetAccessCodeFeatureConfigResponse {
    getAccessCodeFeatureConfig: string;
}

export const getAccessCodeFeatureConfig = async (): Promise<string> => {
    try {
        const { data } = await client.query<GetAccessCodeFeatureConfigResponse>({
            query: GET_ACCESS_CODE_FEATURE_CONFIG,
            fetchPolicy: 'cache-first' // 使用缓存优先策略，减少请求
        });
        return data?.getAccessCodeFeatureConfig ?? '';
    } catch (error) {
        console.error('Failed to get access code feature config:', error);
        return '';
    }
};

/**
 * 检查当前司机是否启用门禁码功能（前端灰度判断）
 * @param dspId 司机的 DSP ID
 * @param configValue 配置值（如 "All" 或 "1,2,3"）
 * @returns true 表示启用，false 表示未启用
 */
export const checkAccessCodeFeatureEnabled = (dspId: string | number | null | undefined, configValue: string): boolean => {
    // 1. DSP ID 为空，返回 false
    if (!dspId) {
        return false;
    }

    // 2. 配置值为空，返回 false（默认禁用）
    if (!configValue || configValue.trim() === '') {
        return false;
    }

    // 3. 配置值为 "all"（不区分大小写），返回 true
    const normalizedValue = configValue.trim().toLowerCase();
    if (normalizedValue === 'all') {
        return true;
    }

    // 4. 解析配置值为 DSP ID 列表（逗号分隔）
    const dspIdStr = String(dspId);
    const whitelistDspIds = normalizedValue
        .split(',')
        .map(id => id.trim())
        .filter(id => id !== '');

    // 5. 检查当前 DSP ID 是否在白名单中
    return whitelistDspIds.includes(dspIdStr);
};

export const GET_ACCESS_CODES_AND_COMMUNITY_MAP = gql`
    query AccessCodeQuery($orderId: Long!) {
        getAccessCodesAndCommunityMap(orderId: $orderId) {
            accessCodes {
                id
                addressId
                codeType
                accessCode
                status
                latitude
                longitude
                validFeedbackCount
                invalidFeedbackCount
                lastAccessCodeUpdatedAt
                accessCodeCreatedAt
                accessCodeUpdatedAt
                currentUserFeedback
                currentDriverCreatedAt
                currentDriverUpdatedAt
            }
            residentialAreaImages {
                id
                residentialAreaId
                imageS3Key
                stopNumber
                imageDisplayUrl
            }
        }
    }
`;

export interface AccessCodeWithStatsType {
    id: number;
    addressId: number;
    codeType: string;
    accessCode: string | null;
    status: number | null; // 状态枚举值：0-待验证，1-可用，2-不可用
    latitude: number | null;
    longitude: number | null;
    validFeedbackCount: number;
    invalidFeedbackCount: number;
    lastAccessCodeUpdatedAt: number | null; // unix timestamp (seconds)
    accessCodeCreatedAt: number | null; // unix timestamp (seconds)
    accessCodeUpdatedAt: number | null; // unix timestamp (seconds)
    currentUserFeedback: number; // 0=未反馈，1=有效反馈，2=无效反馈
    currentDriverCreatedAt: number | null; // 当前司机创建该code的时间（unix timestamp秒数，用于前端24小时频率限制校验）
    currentDriverUpdatedAt: number | null; // 当前司机最后更新该code的时间（unix timestamp秒数，用于前端24小时频率限制校验）
}

export interface AddressResidentialAreaImageType {
    id: string;
    residentialAreaId: number;
    imageS3Key: string;
    stopNumber: number;
    imageDisplayUrl: string | null;
}

export interface AddressAccessCodesResponseType {
    accessCodes: AccessCodeWithStatsType[];
    residentialAreaImages: AddressResidentialAreaImageType[] | null;
}

export interface GetAccessCodesByOrderIdResponse {
    getAccessCodesAndCommunityMap: AddressAccessCodesResponseType;
}

export interface GetAccessCodesByOrderIdVariables {
    orderId: number;
}

export const getAccessCodesByOrderId = async (orderId: number): Promise<AccessCodeWithStatsType[]> => {
    const { data } = await client.query<GetAccessCodesByOrderIdResponse, GetAccessCodesByOrderIdVariables>({
        query: GET_ACCESS_CODES_AND_COMMUNITY_MAP,
        variables: { orderId },
        fetchPolicy: 'network-only'
    });
    return data?.getAccessCodesAndCommunityMap?.accessCodes || [];
};

/**
 * 获取订单的门禁码和地址图片信息
 */
export const getAccessCodesWithImages = async (orderId: number): Promise<AddressAccessCodesResponseType> => {
    const { data } = await client.query<GetAccessCodesByOrderIdResponse, GetAccessCodesByOrderIdVariables>({
        query: GET_ACCESS_CODES_AND_COMMUNITY_MAP,
        variables: { orderId },
        fetchPolicy: 'network-only'
    });
    return data?.getAccessCodesAndCommunityMap || { accessCodes: [], residentialAreaImages: null };
};

export const GET_PENDING_ACCESS_CODES_FOR_DRIVER = gql`
    query GetPendingAccessCodesForDriver($orderId: Long!, $driverAccountId: Long!) {
        getPendingAccessCodesForDriver(orderId: $orderId, driverAccountId: $driverAccountId) {
            id
            addressId
            codeType
            accessCode
            status
            latitude
            longitude
            validFeedbackCount
            invalidFeedbackCount
            lastAccessCodeUpdatedAt
            accessCodeCreatedAt
            accessCodeUpdatedAt
            currentUserFeedback
            currentDriverCreatedAt
            currentDriverUpdatedAt
        }
    }
`;

export interface GetPendingAccessCodesForDriverResponse {
    getPendingAccessCodesForDriver: AccessCodeWithStatsType[];
}

export interface GetPendingAccessCodesForDriverVariables {
    orderId: number;
    driverAccountId: number;
}

export const getPendingAccessCodesForDriver = async (orderId: number, driverAccountId: number): Promise<AccessCodeWithStatsType[]> => {
    const { data } = await client.query<GetPendingAccessCodesForDriverResponse, GetPendingAccessCodesForDriverVariables>({
        query: GET_PENDING_ACCESS_CODES_FOR_DRIVER,
        variables: { orderId, driverAccountId },
        fetchPolicy: 'network-only'
    });
    return data?.getPendingAccessCodesForDriver || [];
};

